import { useId, useMemo, type CSSProperties } from 'react';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useDialogA11y } from './hooks/useDialogA11y';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

export interface PrivacyNoticeProps {
  onDismiss: () => void;
}

const FOOTER_LINK_CLASS = 'privacy-notice-footer-link';

export function PrivacyNotice({ onDismiss }: PrivacyNoticeProps) {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  // NA-205: privacy body copy holds 20px through mid-range, drops to 16px ≤576px.
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const headingId = useId();
  const styles = useMemo(() => createStyles(tokens, isMobile, isSmallMobile), [tokens, isMobile, isSmallMobile]);
  const dialogRef = useDialogA11y<HTMLDivElement>({ onDismiss });

  return (
    <div style={styles.backdrop}>
      <div style={styles.backdropInner}>
        <div style={styles.boxCenterer}>
          <div ref={dialogRef} style={styles.box} role="dialog" aria-modal="true" aria-labelledby={headingId}>
            <button
              type="button"
              style={styles.closeButton}
              onClick={onDismiss}
              aria-label={t('privacy_notice.aria_dismiss')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <line x1="14.8492" y1="0.707107" x2="0.70706" y2="14.8492" stroke={tokens.text.primary} strokeWidth="2"/>
                <line x1="14.8492" y1="0.707107" x2="0.70706" y2="14.8492" stroke="black" strokeOpacity="0.2" strokeWidth="2"/>
                <line x1="14.8495" y1="14.8492" x2="0.7074" y2="0.70706" stroke={tokens.text.primary} strokeWidth="2"/>
                <line x1="14.8495" y1="14.8492" x2="0.7074" y2="0.70706" stroke="black" strokeOpacity="0.2" strokeWidth="2"/>
              </svg>
            </button>
            <div style={styles.boxContent}>
              <h2 id={headingId} style={styles.title}>{t('privacy_notice.title')}</h2>
              <p style={styles.paragraph}>{t('privacy_notice.p1')}</p>
              <p style={styles.paragraph}>{t('privacy_notice.p2')}</p>
              <p style={styles.paragraph}>{t('privacy_notice.p3')}</p>
              <p style={styles.paragraphLast}>{t('privacy_notice.p4')}</p>
            </div>
          </div>
        </div>
        <footer style={styles.footer}>
          <style>{`.${FOOTER_LINK_CLASS}:hover,.${FOOTER_LINK_CLASS}:focus-visible{text-decoration:underline !important;}`}</style>
          {isMobile ? (
            <>
              <div>© {new Date().getFullYear()} Emtrain</div>
              <div>
                <a href="https://emtrain.com/emtrain-privacy-policy/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.privacy')}</a>
                <span style={styles.footerSeparator} aria-hidden="true">|</span>
                <a href="https://emtrain.com/terms-of-service/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.terms_of_service')}</a>
                <span style={styles.footerSeparator} aria-hidden="true">|</span>
                <a href="https://emtrain.com/accessibility/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.accessibility')}</a>
              </div>
            </>
          ) : (
            <>
              <span>© {new Date().getFullYear()} Emtrain</span>
              <span style={styles.footerSeparator} aria-hidden="true">|</span>
              <a href="https://emtrain.com/emtrain-privacy-policy/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.privacy')}</a>
              <span style={styles.footerSeparator} aria-hidden="true">|</span>
              <a href="https://emtrain.com/terms-of-service/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.terms_of_service')}</a>
              <span style={styles.footerSeparator} aria-hidden="true">|</span>
              <a href="https://emtrain.com/accessibility/" target="_blank" rel="noopener noreferrer" style={styles.footerLink} className={FOOTER_LINK_CLASS}>{t('privacy_notice.accessibility')}</a>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function createStyles(tokens: ThemeTokens, isMobile: boolean, isSmallMobile: boolean): Record<string, CSSProperties> {
  return {
    backdrop: {
      position: 'fixed',
      inset: 0,
      overflowY: 'auto',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      zIndex: 9999,
    },
    backdropInner: {
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    boxCenterer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...(isMobile ? { padding: tokens.spacing.lg } : {}),
    },
    box: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '15px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.borders.default,
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
      overflow: 'auto',
      fontFamily: tokens.typography.fontFamily,
      ...(isMobile
        ? { width: '100%' }
        : { width: '953px', minHeight: '648px' }),
    },
    boxContent: {
      padding: isMobile ? tokens.spacing.xl : '58px 63px',
    },
    closeButton: {
      position: 'absolute',
      top: '17px',
      right: '16px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: tokens.spacing.sm,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: isMobile ? tokens.typography.mobile.h3.size : tokens.typography.h3.size,
      fontWeight: tokens.typography.weights.bold,
      color: tokens.text.primary,
      margin: `0 0 ${isMobile ? tokens.spacing.lg : tokens.spacing.lgPlus}`,
      lineHeight: tokens.typography.lineHeights.tight,
    },
    paragraph: {
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.scale,
      margin: `0 0 ${isMobile ? tokens.spacing.sm : '10px'}`,
    },
    paragraphLast: {
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.scale,
      margin: 0,
    },
    footer: {
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: tokens.borders.default,
      padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
      textAlign: 'center',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
    },
    footerSeparator: {
      margin: `0 ${tokens.spacing.sm}`,
    },
    footerLink: {
      color: 'inherit',
      textDecoration: 'none',
    },
  };
}
